import {UseFormReturn} from 'react-hook-form';

import {BaseVehicle} from '../../types/BaseVehicle';

export interface FormClearPhevFieldsActionParams {
  form: UseFormReturn<BaseVehicle>;
  previousFuelType: string | null;
  previousHybridType: string | null;
}

/**
 * Manages field clearing when transitioning to/from PHEV fuel type:
 * - When transitioning TO PHEV: Clears basic CO2 fields (emission, class)
 * - When transitioning AWAY from PHEV: Clears PHEV-specific fields (weighted combined, discharged)
 *
 * @param _context - XState context (unused)
 * @param params - Parameters containing form instance and previous fuel type values
 */
export const formClearPhevFieldsAction = (
  _context: unknown,
  params: FormClearPhevFieldsActionParams
): void => {
  const {form, previousFuelType, previousHybridType} = params;

  const values = form.getValues();
  const currentFuelType = values.fuelType?.[0] ?? null;
  const currentHybridType = values.hybridType?.[0] ?? null;

  const wasPHEV =
    previousFuelType === 'FUELTYPE_HYBRID' && previousHybridType === 'HYBRIDTYPE_PHEV';
  const isPHEV = currentFuelType === 'FUELTYPE_HYBRID' && currentHybridType === 'HYBRIDTYPE_PHEV';

  // If transitioning away from PHEV, clear all PHEV-specific fields
  if (wasPHEV && !isPHEV) {
    // Only clear hybridType when leaving the Hybrid fuel type entirely.
    // When switching between hybrid subtypes (e.g. PHEV → Full-hybrid),
    // preserve the newly selected hybridType value.
    if (currentFuelType !== 'FUELTYPE_HYBRID') {
      form.setValue('hybridType', [], {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
    // Clear PHEV-only fields
    form.setValue('fuelConsumptionWltpWeighted', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('fuelConsumptionWltpWeightedCombinedKwh', null, {
      shouldDirty: false,
      shouldValidate: false,
    });

    // Always clear all electric kWh fields when leaving PHEV
    form.setValue('fuelConsumptionWltpCombinedKwh', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('fuelConsumptionWltpCityCenterKwh', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('fuelConsumptionWltpOutskirtsKwh', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('fuelConsumptionWltpCountryRoadsKwh', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('fuelConsumptionWltpHighwayKwh', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('carbonDioxideEmissionWeightedCombined', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('carbonDioxideEmissionClassWeightedCombined', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('carbonDioxideEmissionDischarged', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('carbonDioxideEmissionClassDischarged', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }

  // If transitioning TO PHEV, clear basic CO2 fields
  // PHEV uses weighted combined and discharged variants instead
  if (!wasPHEV && isPHEV) {
    form.setValue('carbonDioxideEmission', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue('carbonDioxideEmissionClass', null, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }
};
